import { useRef } from 'react';
import { useUser } from '../redux/reducer/user';
import { useContent } from '../redux/reducer/content';
import { useClothType } from './useClothType';
import { useRequestsHandlerData } from './useRequestsHandlerData';
import { getSlots } from '../utils/getSlots';
import { allSlots } from '../constants/elf3d';
import { useApplicationCallbacks } from '../providers/application-callbacks';
import { CHARACTER, ROOM } from '../constants/shop';
import { useErrorModalHandler } from './error/useErrorModalHandler';

export const useBuyOrPutItem = ({ updateBeautiesBalance = false } = {}) => {
  const { profile } = useUser();
  const { notFilteredShopItems } = useContent();
  const getClothTypeById = useClothType();
  const callbacks = useApplicationCallbacks();
  const showErrorModal = useErrorModalHandler();

  const shopRequest = (updatedItems) => {
    callbacks.userChange({ key: 'profile', value: { ...profile, items: updatedItems } });
  };

  const tempArg = useRef(null);

  const updateOnBuyItem = () => {
    const itemType = getClothTypeById(tempArg.current);

    const callbackKey = `add${
      {
        room: 'OutsideAnimations',
        character: 'Animations',
        shower: 'Animations',
      }[itemType]
    }`;

    callbacks[callbackKey]([
      {
        state: { room: 'main', shower: 'main', character: 'customization' }[itemType],
        animation: { room: 'interior', shower: 'interior', character: 'outerwear' }[itemType],
      },
    ]);

    const necessaryItem = { ...notFilteredShopItems?.find(({ id }) => id === tempArg.current), is_equipped: true };
    const necessaryItemSlots = getSlots(necessaryItem);
    const isHaveNecessaryItem = profile?.items?.some(({ id }) => id === necessaryItem?.id);
    const updatedProfileItems = isHaveNecessaryItem
      ? profile?.items?.map((item) => ({
          ...item,
          is_equipped: item?.id === necessaryItem?.id || item?.is_equipped,
        }))
      : [...profile?.items, ...(isHaveNecessaryItem ? [] : [necessaryItem])];

    const equipLogic = {
      shower() {
        return updatedProfileItems?.map((item) => {
          if (item?.slot !== necessaryItem?.slot) return item;
          const is_equipped = item?.id === necessaryItem?.id;
          return { ...item, is_equipped };
        });
      },
      room() {
        return updatedProfileItems?.map((item) => {
          if (item?.id === necessaryItem?.id || getClothTypeById(item.id) === CHARACTER) return item;
          const allItemSlots = getSlots(item);
          const isMerge = allItemSlots?.some((slot) => necessaryItemSlots?.includes(slot));
          const is_equipped = !isMerge && item?.is_equipped;
          return { ...item, is_equipped };
        });
      },
      character() {
        const newProfileItems = updatedProfileItems?.map((item) => {
          if (item?.id === necessaryItem?.id || getClothTypeById(item.id) === ROOM) return item;
          const allItemSlots = getSlots(item);
          const isMerge = allItemSlots?.some((slot) => necessaryItemSlots?.includes(slot));
          const is_equipped = !isMerge && item?.is_equipped;
          return { ...item, is_equipped };
        });

        const allSlotsOnElf = newProfileItems?.reduce((acc, item) => {
          if (!item.is_equipped || getClothTypeById(item.id) === ROOM) return acc;
          const allSlots = getSlots(item);
          return [...new Set([...acc, ...allSlots])];
        }, []);

        const toDefaults = allSlots.filter((slot) => !allSlotsOnElf.includes(slot));

        const defaultsIds = toDefaults?.map((slot) => {
          return newProfileItems?.find((item) => item.slot === slot && item?.is_default)?.id;
        });

        return newProfileItems?.map((item) => {
          if (!defaultsIds?.includes(item.id)) return item;
          return { ...item, is_equipped: true };
        });
      },
    };

    return shopRequest(equipLogic[itemType]?.());
  };

  return useRequestsHandlerData([
    {
      address: 'requestsUserBeautiesBalance',
    },
    { address: 'requestsUserSetActiveRoom' },
    ...['requestsShopEquipItem', 'requestsShopBuyItem'].map((address) => ({
      address,
      fulfilled: ({ meta: { arg } }) => {
        tempArg.current = arg;

        // Если нужно дополнительно запросить баланс бьютисов (обновленный)
        if (updateBeautiesBalance) {
          updateOnBuyItem();
          callbacks.getBeautiesBalance();
        } else {
          updateOnBuyItem();
        }
      },
      rejected(data) {
        const { response } = data;
        showErrorModal(response.data);
      },
    })),
  ]);
};
